import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  canOfferTranslation,
  getTranslatableLocales,
  normalizeAppLocale,
  translateTexts,
} from '../lib/translateApi'

interface Props {
  /** Stable key for this report so translations reset when switching entries. */
  entryKey: string
  sourceLocale: string | null | undefined
  fields: Record<string, string | null | undefined>
  children: (values: Record<string, string>) => ReactNode
}

/**
 * Offers one-click machine translation of open text fields when the viewer’s
 * UI language differs from the language used when the report was written.
 * Pairs the server cannot translate (e.g. Papiamento without Google Translate)
 * are not offered at all — no unsupported notice.
 */
export function ReportTranslation({
  entryKey,
  sourceLocale,
  fields,
  children,
}: Props) {
  const { t, i18n } = useTranslation()
  const uiLocale = normalizeAppLocale(i18n.resolvedLanguage)
  const source = normalizeAppLocale(sourceLocale)
  const [translatableLocales, setTranslatableLocales] = useState<string[]>([
    'en',
    'es',
    'nl',
  ])

  useEffect(() => {
    let cancelled = false
    void getTranslatableLocales().then((locales) => {
      if (!cancelled) setTranslatableLocales(locales)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const offer = canOfferTranslation(source, uiLocale, translatableLocales)

  const originals: Record<string, string> = {}
  for (const [key, value] of Object.entries(fields)) {
    originals[key] = value?.trim() ?? ''
  }
  const textsToTranslate = Object.fromEntries(
    Object.entries(originals).filter(([, value]) => value.length > 0),
  )
  const hasText = Object.keys(textsToTranslate).length > 0

  const [showTranslated, setShowTranslated] = useState(false)
  const [translated, setTranslated] = useState<Record<string, string> | null>(
    null,
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setShowTranslated(false)
    setTranslated(null)
    setLoading(false)
    setError(null)
  }, [entryKey, source, uiLocale])

  async function handleTranslate() {
    if (loading) return
    if (translated) {
      setShowTranslated(true)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const result = await translateTexts({
        source_locale: source,
        target_locale: uiLocale,
        texts: textsToTranslate,
      })
      setTranslated(result.translations)
      setShowTranslated(true)
    } catch (caught) {
      setError(
        caught instanceof Error && caught.message
          ? caught.message
          : t('translate.failed'),
      )
    } finally {
      setLoading(false)
    }
  }

  const display: Record<string, string> = { ...originals }
  if (showTranslated && translated) {
    for (const [key, value] of Object.entries(translated)) {
      if (value) display[key] = value
    }
  }

  return (
    <div className="report-translation">
      {offer && hasText && (
        <div className="report-translation-bar">
          {!showTranslated ? (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => void handleTranslate()}
              disabled={loading}
            >
              {loading
                ? t('translate.working')
                : t('translate.offer', { language: t(`lang.${uiLocale}`) })}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setShowTranslated(false)}
            >
              {t('translate.showOriginal', {
                language: t(`lang.${source}`),
              })}
            </button>
          )}
          {showTranslated && (
            <span className="report-translation-note">
              {t('translate.machineNote')}
            </span>
          )}
          {error && (
            <span className="field-error" role="alert">
              {error}
            </span>
          )}
        </div>
      )}
      {children(display)}
    </div>
  )
}
